import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { useRouter } from "next/router";
import { ShopMatchReception } from "@components/ShopMatchReception";
import type { ShopMatchReception_ShopMatchFragment } from "@components/ShopMatchReception/__generated__/fragments";
import { ShopMatchReceptionStateEnum } from "@graphql/__generated__/types";
import { getRouter as getRewriteRouter } from "@libs/rewrite";
import { CalendarContainer } from "./CalendarContainer";
import styles from "./index.module.css";
import type { ShopCardLarge_ShopFragment as ShopListItemFragment } from "../__generated__/fragments";
import type { FC } from "react";

dayjs.extend(isBetween);

type Props = {
  shop: ShopListItemFragment;
  shopMatch?: ShopMatchReception_ShopMatchFragment;
};

// 以下の条件によってShopLargeCardのFooterに表示するものを切り替える
// - 試合で絞り込んでいる時
//   - SP,PC共通: 条件に応じて「ネット予約する」「満席」「電話で確認」などのボタンを表示する(条件の詳細は同ファイル内の関数「shouldShowShopMatchReception」を参照)
// - 試合絞り込み以外の時
//   - SP,PC共通: ネット予約できる場合は「カレンダー」を表示する
//   - SP,PC共通: ネット予約できない場合は何も表示しない（ネット予約機能オフ or ネット予約機能はオンだけど全部「-」「満席」「TEL」表記のとき）
// - 上記以外
//   - 非表示
export const ShopCardLargeFooter: FC<Props> = ({ shop, shopMatch }) => {
  const router = useRouter();
  const rewriteRouter = getRewriteRouter();
  const result = rewriteRouter.match(router.asPath);
  const isStaticUrl = !!result;
  const isSpecifiedMatch = isStaticUrl
    ? !!result?.params?.matchId
    : !!router.query.match;

  if (!isSpecifiedMatch) {
    return shop.isReservableOnline ? <CalendarContainer shop={shop} /> : null;
  }

  if (!shopMatch) {
    return null;
  }

  if (
    !shouldShowShopMatchReception({
      receptionState: shopMatch.receptionState,
      receptionStartsAt: shopMatch.receptionStartsAt ?? null,
      receptionEndsAt: shopMatch.receptionEndsAt ?? null,
    })
  ) {
    return null;
  }

  return (
    <div className={styles.reservation}>
      <ShopMatchReception
        shopMatch={shopMatch}
        shopUuid={shop.uuid}
        shopName={shop.name}
        shopTel={shop.tel}
        shopTelWithHyphen={shop.telWithHyphen}
      />
    </div>
  );
};

// Receptionボタン部分の表示・非表示のフラグを返す。
// 表示・非表示の条件に関しては以下リンクのシートを参照。
// cf. https://docs.google.com/spreadsheets/d/1QPSpKvGMp5KaEm6p6kx012fIGJhbG2GLPVrWjBAe9oQ/edit?gid=104501591#gid=104501591
const shouldShowShopMatchReception = ({
  receptionState,
  receptionStartsAt,
  receptionEndsAt,
}: {
  receptionState: ShopMatchReceptionStateEnum;
  receptionStartsAt: string | null;
  receptionEndsAt: string | null;
}): boolean => {
  const isInReceptionPeriod =
    receptionStartsAt &&
    receptionEndsAt &&
    dayjs().isBetween(receptionStartsAt, receptionEndsAt, "second", "[]");

  // 以下の場合はいかなる時間帯でも非表示
  // - リクエスト予約機能がOFF
  // - 予約受付期間前
  // - 予約を受け付けていない
  if (
    receptionState === ShopMatchReceptionStateEnum.TelByReservationDisabled ||
    receptionState === ShopMatchReceptionStateEnum.BeforeReception ||
    receptionState === ShopMatchReceptionStateEnum.Unacceptable
  ) {
    return false;
  }

  // 受付期間中で電話のみ受付可の場合は非表示
  if (
    isInReceptionPeriod &&
    receptionState === ShopMatchReceptionStateEnum.Tel
  ) {
    return false;
  }

  return true;
};
